import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from "lucide-react";
import { COMPANY, SERVICES } from "../data/site";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#111812] text-[#F7F5F0] tf-grain relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center justify-center w-16 h-12 shrink-0">
                <img
                  src="/images/logo-mark-transparent.png"
                  alt="Trädfällarna Laholm AB"
                  loading="lazy"
                  className="w-full h-full object-contain brightness-0 invert"
                />
              </span>
              <div>
                <div className="font-heading font-extrabold text-[17px] text-white">
                  Trädfällarna
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#A8B0A4] font-semibold">
                  Laholm AB
                </div>
              </div>
            </div>
            <p className="text-[#C8CDC4] text-sm leading-relaxed max-w-xs">
              Lokalt företag i Laholm med fokus på säker trädfällning, beskärning
              och stubbfräsning. Tydlig offert, rätt utrustning och fokus på
              tryggt utfört arbete.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={COMPANY.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Trädfällarna Laholm på Facebook"
                data-testid="footer-social-facebook"
                className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white/5 border border-white/10 text-[#E5E7E3] hover:bg-[#D95D2E] hover:text-white hover:border-[#D95D2E] transition-colors"
              >
                <Facebook className="w-4 h-4" strokeWidth={2.2} />
              </a>
              <a
                href={COMPANY.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Trädfällarna Laholm på Instagram"
                data-testid="footer-social-instagram"
                className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white/5 border border-white/10 text-[#E5E7E3] hover:bg-[#D95D2E] hover:text-white hover:border-[#D95D2E] transition-colors"
              >
                <Instagram className="w-4 h-4" strokeWidth={2.2} />
              </a>
              <span className="text-xs text-[#A8B0A4] uppercase tracking-[0.16em] font-semibold">
                Följ oss
              </span>
            </div>
          </div>

          {/* Tjänster */}
          <div>
            <h4 className="font-heading text-sm uppercase tracking-[0.18em] text-[#D95D2E] font-bold mb-4">
              Tjänster
            </h4>
            <ul className="space-y-2.5 text-[15px]">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}`}
                    data-testid={`footer-link-${s.slug}`}
                    className="text-[#E5E7E3] hover:text-[#D95D2E] transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="font-heading text-sm uppercase tracking-[0.18em] text-[#D95D2E] font-bold mb-4">
              Kontakt
            </h4>
            <ul className="space-y-3 text-[15px]">
              <li>
                <a
                  href={`tel:${COMPANY.phoneE164}`}
                  data-testid="footer-phone"
                  className="flex items-start gap-3 text-[#E5E7E3] hover:text-[#D95D2E] transition-colors"
                >
                  <Phone className="w-4 h-4 mt-1 shrink-0 text-[#D95D2E]" />
                  <span>
                    <span className="block text-xs text-[#A8B0A4] uppercase tracking-wider">
                      Telefon
                    </span>
                    {COMPANY.phone}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="footer-email"
                  className="flex items-start gap-3 text-[#E5E7E3] hover:text-[#D95D2E] transition-colors"
                >
                  <Mail className="w-4 h-4 mt-1 shrink-0 text-[#D95D2E]" />
                  <span>
                    <span className="block text-xs text-[#A8B0A4] uppercase tracking-wider">
                      E-post
                    </span>
                    {COMPANY.email}
                  </span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-[#E5E7E3]">
                <MapPin className="w-4 h-4 mt-1 shrink-0 text-[#D95D2E]" />
                <span>
                  <span className="block text-xs text-[#A8B0A4] uppercase tracking-wider">
                    Adress
                  </span>
                  {COMPANY.address.street}
                  <br />
                  {COMPANY.address.zip} {COMPANY.address.city}
                </span>
              </li>
            </ul>
          </div>

          {/* Företag */}
          <div>
            <h4 className="font-heading text-sm uppercase tracking-[0.18em] text-[#D95D2E] font-bold mb-4">
              Företag
            </h4>
            <ul className="space-y-3 text-[15px] text-[#E5E7E3]">
              <li>
                <span className="block text-xs text-[#A8B0A4] uppercase tracking-wider">
                  Org. nummer
                </span>
                {COMPANY.org}
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-1 shrink-0 text-[#D95D2E]" />
                <span>
                  <span className="block text-xs text-[#A8B0A4] uppercase tracking-wider">
                    Verksamhetsområde
                  </span>
                  Laholm med omnejd
                </span>
              </li>
              <li className="pt-2">
                <Link
                  to="/kontakt#offert"
                  data-testid="footer-cta-quote"
                  className="inline-flex items-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors"
                >
                  Få offert
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-[#283224] flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-xs text-[#A8B0A4]">
          <div>
            Hemsida skapad av{" "}
            <a
              href="https://elido.nu"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-elido-link"
              className="text-[#E5E7E3] hover:text-white underline-offset-2 hover:underline transition-colors"
            >
              Elido
            </a>
          </div>
          <nav
            aria-label="Juridik"
            className="flex flex-wrap items-center gap-x-5 gap-y-2"
          >
            <Link
              to="/integritetspolicy"
              data-testid="footer-link-integritetspolicy"
              className="hover:text-[#E5E7E3] transition-colors"
            >
              Integritetspolicy
            </Link>
            <Link
              to="/cookiepolicy"
              data-testid="footer-link-cookiepolicy"
              className="hover:text-[#E5E7E3] transition-colors"
            >
              Cookiepolicy
            </Link>
            <span>
              © {new Date().getFullYear()} {COMPANY.name}
            </span>
          </nav>
        </div>
      </div>
    </footer>
  );
}
